/**
 * =============================================================================
 * THE B4 SEAM — ONE LINE, AND IT IS DELIBERATELY IN A FILE B4 DOES NOT OWN
 * =============================================================================
 *
 * Phase 2's web build is split between two owners: this work stream owns
 * `apps/web/**` EXCEPT `src/features/inquiries/**`, which is B4's (table, board,
 * peek, record, new-inquiry, follow-up).
 *
 * `src/routes.tsx` cannot `import` from `src/features/inquiries/` directly, because
 * a bundler resolves that path at BUILD time: until B4's module exists, every
 * `pnpm build` and every `pnpm test` in this app would fail on a missing file — the
 * shell would be unbuildable because a different work stream had not landed yet.
 * A dynamic `import()` does not help; Rollup resolves those statically too.
 *
 * So the seam lives here, in a file this work stream owns, as an empty array. When
 * the inquiries feature lands, mounting it is exactly this edit:
 *
 *     import { inquiriesRoutes } from './features/inquiries/routes';
 *     export const extraCountryRoutes: RouteObject[] = inquiriesRoutes;
 *
 * The routes it should provide, so both halves agree without a conversation:
 *
 *     { path: 'inquiries',            element: <InquiriesListPage /> }   // table + board
 *     { path: 'inquiries/:inquiryId', element: <InquiryRecordPage /> }   // the Record page
 *
 * Both are mounted INSIDE `RequireCountry` + `AppShell`, so they can rely on
 * `useCountryCode()` and on the session being resolved. The nav entry and the
 * stage-count cards already link to `/countries/:cc/inquiries`; until this array is
 * filled, those links land on the shell's not-found state, which says so plainly
 * rather than rendering a blank page.
 */

import type { RouteObject } from 'react-router-dom';
import { inquiriesRoutes } from './features/inquiries/routes';
import { adminInfraRoutes } from './features/admin/routes.infra';
import { adminUsersRoutes } from './features/admin/routes.users';
import { adminRolesRoutes } from './features/admin/routes.roles';
import { adminCatalogueRoutes } from './features/admin/routes.catalogue';
import { adminTaxRoutes } from './features/admin/routes.tax';
import { adminVocabularyRoutes } from './features/admin/routes.vocabulary';
import { quoteRoutes } from './features/quotes/routes';
import { partiesRoutes } from './features/parties/routes';
import { partyCallRoutes } from './features/party-calls/routes';
import { settingsRoutes } from './features/settings/routes';
import { workPipelineRoutes } from './features/work-pipeline/routes';
import { crfRoutes } from './features/crfs/routes';
import { registerRoutes } from './features/registers/routes';
import { paymentRoutes } from './features/payments/routes';
import { renewalsRoutes } from './features/renewals/routes';
import { tallyLicenceRoutes } from './features/tally-licences/routes';
import { fieldAdminRoutes, fieldRoutes } from './features/field/routes';
// v19 · W2 (delimited): the group dashboard + the four Figma 06 reports.
import { dashboardsRoutes } from './features/dashboards/routes';
import { reportRoutes } from './features/reports/routes';
// ── v19 DASHBOARDS · W1 (BEGIN W1) ──
import { teamDashboardRoutes } from './features/dashboards/team-routes';
// ── v19 DASHBOARDS · W1 (END W1) ──

/**
 * B4 has landed, so the seam is filled by exactly the edit the comment above described.
 *
 * `inquiriesRoutes` provides the two paths it named — `inquiries` and
 * `inquiries/:inquiryId` — wrapped in one provider layout route so a toast (and therefore an
 * Undo) survives navigation between them.
 *
 * Phase 2.5b adds the Admin surface through the same seam. It arrives in
 * slices, one route array per work stream, so neither stream edits the other's
 * file: `adminInfraRoutes` (agent keys, network rules, go-live) is the
 * infrastructure slice; `adminUsersRoutes` (users list, create, detail) is the
 * users/roles slice — its routing rationale lives in its own header.
 *
 * Phase 3a · U5 arrives through the same seam. `quoteRoutes` carries the five
 * sales paths — the quote list, the builder, the quote record, and the two
 * read-only sales-order screens — behind one `ToastProvider` layout, so a toast
 * raised by confirming a quote survives the navigation that confirmation causes.
 */
export const extraCountryRoutes: RouteObject[] = [
  ...inquiriesRoutes,
  ...quoteRoutes,
  /*
   * WEB TIER A · WT6 — `parties/new`, the New-customer page the pickers' create
   * rows route to. A ROUTE, not a nav destination: the `parties` nav row stays
   * `available:false` (phase 3), so plan §11's "no new IA" holds.
   */
  ...partiesRoutes,
  /*
   * OVERNIGHT v20 · D2 — the PARTY CALL LOG: `party-calls` (the country register) and
   * `parties/:partyId/calls` (one customer's history panel). ROUTES, not nav
   * destinations — the sidebar registry is an IA decision the owner rules on, and the
   * `parties/new` line above is the precedent. Both are reached from the Parties
   * report's customer column and by URL. Both gated on `parties.view` at the server;
   * the screens render the designed refusal rather than a blank page.
   */
  ...partyCallRoutes,
  /*
   * WEB TIER A · WT7 — `settings`, the Profile+Appearance page. Like
   * `parties/new` above it is a ROUTE and not a nav destination: `nav.ts`
   * carries no settings item and gains none (plan §11), and the way in is the
   * shell's identity menu.
   */
  ...settingsRoutes,
  ...crfRoutes,
  ...registerRoutes,
  ...paymentRoutes,
  ...renewalsRoutes,
  /*
   * ── v19 · W2 — delimited block ───────────────────────────────────────────────
   * `dashboardsRoutes` carries `group` (the FX-gated group view, a global surface);
   * `reportRoutes` carries the four paths `nav.ts` reserved in Phase 3 —
   * `reports/received`, `reports/complete`, `billing`, `parties` — whose rows flip
   * `available: true` in the same change. The country dashboard itself EXTENDS the
   * index `DashboardPage` and needs no route here.
   */
  ...dashboardsRoutes,
  ...reportRoutes,
  /* ── end v19 · W2 block ───────────────────────────────────────────────────────── */
  /*
   * The licence register arrives through the same seam: three read-only screens
   * (`licences`, `licences/by-serial/:serial`, `parties/:partyId/licences`) with
   * no write route behind any of them. Its own header records why the third is
   * mounted before the party page that will hold it exists.
   */
  ...tallyLicenceRoutes,
  ...workPipelineRoutes,
  /*
   * v17 FIELD TRACKING (F4): the two field registers, the day timeline, and —
   * in the admin block below — the per-country field settings. Same seam,
   * same shape: `fieldRoutes` carries the three read screens, and the
   * timeline is a route without a nav row (its own header records why).
   */
  ...fieldRoutes,
  /*
   * ── v19 DASHBOARDS · W1 (BEGIN W1) ── the Team section: `team` (the dev-head
   * workload view) and `team/throughput` (who does what). Both are BARE-route
   * surfaces scoped by the token, mounted under the country like the global admin
   * screens are; `team-routes.tsx` records why.
   */
  ...teamDashboardRoutes,
  // ── v19 DASHBOARDS · W1 (END W1) ──
  ...adminInfraRoutes,
  ...adminUsersRoutes,
  ...adminRolesRoutes,
  ...adminCatalogueRoutes,
  ...adminTaxRoutes,
  /*
   * The VOCABULARY slice (platforms/stacks authoring + one country's platform
   * availability) — the screens the project draft-trap refusal ("A group
   * administrator must add one before this project can be confirmed",
   * `crfs.service.ts:1774`) sends people to. Its routing rationale lives in its
   * own header (`routes.vocabulary.tsx`).
   */
  ...adminVocabularyRoutes,
  ...fieldAdminRoutes,
];
